import { createContext, useContext } from "react";
import { api } from "./api";

export type SetupStep =
  | "claim_slug"
  | "confirm_inbound"
  | "starter_pack"
  | "escalation"
  | "signature"
  | "complete";

export interface TenantSettings {
  autoRepliesEnabled: boolean;
  dryRunMode: boolean;
  bedrockRegion: string;
  classifierModel: string;
  replyModel: string;
  defaultConfidenceThreshold: number;
  defaultAutoSendThreshold: number;
  escalationDestination: {
    type: "forward" | "webhook" | "review_queue_only";
    forwardTo?: string;
    connectorId?: string | null;
  };
  signature: {
    plainText: string;
    autoDisclaimerEnabled: boolean;
  };
  senderEmail?: string;
  senderEmailStatus?: "pending" | "verified" | "failed";
  senderEmailCheckedAt?: string;
  maxRepliesPerCase: number;
  contentFilteringEnabled: boolean;
  replyAddressMode?: "triagent_subdomain" | "verified_sender";
  ignoredSenders?: string[];
  businessContext?: string;
  slackWebhookUrl?: string;
  slackDailyDigestEnabled?: boolean;
  slackDigestHourLocal?: number;
  slackDigestTimezone?: string;
  outboundArchiveEmail?: string;
  outboundProvider?: "ses" | "smtp";
  smtp?: {
    host: string;
    port: number;
    encryption: "starttls" | "tls";
    username: string;
    fromEmail: string;
    fromName?: string;
    lastVerifiedAt?: string;
  };
}

export interface DashboardStats {
  today: {
    received: number;
    autoReplied: number;
    autoRepliedRate: number;
    escalated: number;
  };
  open: {
    awaitingReview: number;
    awaitingSlotFill: number;
    newOrClassified: number;
  };
  counts: {
    activeIntents: number;
    totalIntents: number;
  };
  topCategories: Array<{ intentId: string; name: string; count: number }>;
  recentEscalations: Array<{
    caseId: string;
    subject: string;
    fromEmail: string;
    updatedAt: string;
    intentName: string | null;
  }>;
}

export interface Me {
  user: { userId: string; email: string; role: string };
  tenant: {
    tenantId: string;
    displayName: string;
    inboundSubdomain: string;
    plan: "trial" | "paid" | "internal";
    status: string;
    quotaEmailsLimit: number;
    quotaEmailsUsed: number;
    trialExpiresAt?: string;
    settings: TenantSettings;
  } | null;
  hasTenant: boolean;
  setupComplete: boolean;
  isSuperAdmin: boolean;
  setupProgress?: {
    currentStep: SetupStep;
    completedSteps: SetupStep[];
    firstInboundReceivedAt: string | null;
    starterPackInstalled: string | null;
  };
}

export interface AdminTenantSummary {
  tenantId: string;
  displayName: string;
  status: string;
  plan: "trial" | "paid" | "internal";
  inboundSubdomain: string;
  quotaEmailsUsed: number;
  quotaEmailsLimit: number;
  trialStartedAt?: string;
  trialExpiresAt?: string;
  ownerEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminStats {
  counts: {
    total: number;
    trialActive: number;
    trialExpired: number;
    paid: number;
    suspended: number;
    expiringSoon: number;
  };
  usage: {
    totalQuota: number;
    totalUsed: number;
  };
}

export interface PackSummary {
  packId: string;
  displayName: string;
  description: string;
  industry: string;
  intentCount: number;
}

export interface CaseSummary {
  caseId: string;
  status: string;
  intentId: string | null;
  intentName?: string | null;
  classificationConfidence: number | null;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  updatedAt: string;
  createdAt: string;
}

export interface IntentMatchingRule {
  type:
    | "subject_contains"
    | "subject_regex"
    | "body_contains"
    | "body_regex"
    | "from_domain_in"
    | "from_email_in"
    | "header_equals";
  value?: string | string[];
  flags?: string;
  caseSensitive?: boolean;
  headerName?: string;
}

export interface IntentSummary {
  intentId: string;
  displayName: string;
  description: string;
  status: "active" | "draft" | "archived";
  priority: number;
  examples: Array<{ subject: string; snippet: string }>;
  matchingRules: IntentMatchingRule[];
  slots: Array<{
    name: string;
    label: string;
    type: string;
    required: boolean;
    requiredGroup?: string;
    extractionHint?: string;
    privacyClass?: "non_pii" | "pii" | "sensitive_pii";
    validationRegex?: string;
    normalizer?: string;
    enumValues?: string[];
  }>;
  confidenceThreshold: number;
  autoSendThreshold: number;
  replyMode: "direct" | "draft";
  gatherMaxAttempts?: number;
  knowledgeContent?: string;
  connectorId?: string | null;
  serviceId?: string | null;
}

export interface EmailReplySummary {
  replyId: string;
  status: "drafted" | "approved" | "sent" | "rejected" | "superseded" | "send_failed";
  generatedBy: "ai" | "human" | "ai_edited_by_human";
  draftedSubject: string;
  draftedBody: string;
  sentBody?: string;
  sentSubject?: string;
  sentAt?: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  rejectionReason?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CaseDetail {
  case: CaseSummary & {
    slotState: Record<string, { value: string | null; confidence?: number; source?: string }>;
    classificationSource: "rule" | "llm" | "manual_reclassify" | null;
  };
  messages: Array<{
    messageId: string;
    direction: "inbound" | "outbound";
    fromEmail: string;
    fromName: string | null;
    subject: string;
    /** Resolved body — the API loads from S3 for new rows, inline for legacy. */
    plainTextBody: string;
    htmlBody?: string;
    /** True when storage was truncated to fit our cap — full original lives in
     *  the .eml accessible via `s3Key` (downloadable through the eml endpoint). */
    bodyTruncated?: boolean;
    /** Pointer to the raw .eml in S3. Only set on inbound messages — outbound
     *  was composed server-side and isn't retained as MIME. UI shows the
     *  download button when this is present. */
    s3Key?: string;
    receivedAt?: string;
    sentAt?: string;
    attachments?: Array<{
      contentType: string;
      filename: string;
      sizeBytes: number;
    }>;
  }>;
  intent: IntentSummary | null;
  replies: EmailReplySummary[];
  /** Connector audit events for this case, oldest-first. Surfaces the
   *  request/response of each connector call in the AI workflow timeline. */
  connectorEvents?: ConnectorEvent[];
}

export interface ConnectorEvent {
  eventId: string;
  eventType: "connector.invoked" | "connector.failed";
  createdAt: string;
  payload: {
    connectorId: string;
    connectorDisplayName?: string;
    urlHost?: string;
    httpStatus?: number;
    durationMs?: number;
    reason?: string;
    message?: string;
    mappedKeys?: string[];
    customerKeys?: string[];
    internalKeys?: string[];
    mapped?: Array<{
      name: string;
      visibility: "customer" | "internal";
      label?: string;
      valuePreview: string;
    }>;
    request?: {
      method: string;
      url: string;
      headers: Record<string, string>;
      body: string | null;
    };
    response?: {
      status?: number;
      durationMs?: number;
      bodyText?: string;
      truncated?: boolean;
    };
  };
}

export interface ReviewItem extends CaseSummary {
  draft: EmailReplySummary | null;
}

export const MeContext = createContext<{
  me: Me;
  refresh: () => Promise<void>;
} | null>(null);

export function useMe() {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe must be used inside <MeProvider>");
  return ctx;
}

export async function fetchMe(): Promise<Me> {
  return api.get<Me>("/api/me");
}
